/**
 * Browse Contractors Page
 * Search and filter contractors by trade, city, and experience
 */

import { BadgeCheck, Clock, Hammer, MapPin } from "lucide-react";
import type * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ImageLightbox } from "@/components/ui/image-lightbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleChip } from "@/components/ui/toggle-chip";
import { CardRating } from "@/features/marketplace/components/card-rating";
import {
	browseContractors,
	type ContractorProfile,
} from "@/features/marketplace/services/marketplace-api.service";
import { useCityFilterOptions, useTrades } from "@/lib/constants/markets";
import { cn, resolveMediaUrl } from "@/lib/utils";

function ContractorCardButton({
	contractor,
}: {
	contractor: ContractorProfile;
}) {
	const { t } = useTranslation();
	const navigate = useNavigate();

	return (
		<button
			type="button"
			className="block w-full text-left cursor-pointer"
			onClick={() => navigate(`/contractors/${contractor.id}`)}
		>
			<div className="flex items-start gap-4">
				{/* Avatar */}
				<div
					className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full text-xl font-display font-bold border border-dashboard-border shadow-[var(--dashboard-shadow-avatar)]"
					style={{
						background: contractor.profilePictureUrl
							? "transparent"
							: "linear-gradient(135deg, var(--color-dashboard-warm-200), var(--color-dashboard-orange-300))",
						color: "var(--color-dashboard-orange-text-on-tint)",
					}}
				>
					{contractor.profilePictureUrl ? (
						<img
							src={contractor.profilePictureUrl}
							alt={contractor.name}
							className="h-16 w-16 rounded-full object-cover"
						/>
					) : (
						contractor.name.charAt(0).toUpperCase()
					)}
				</div>

				{/* Info */}
				<div className="flex-1 min-w-0">
					<div className="flex items-center gap-2">
						<h3 className="font-display text-[20px] font-medium text-dashboard-text group-hover:text-dashboard-accent-dark transition-colors truncate">
							{contractor.name}
						</h3>
						{contractor.isVerified && (
							<BadgeCheck className="h-5 w-5 text-dashboard-accent-dark shrink-0" />
						)}
					</div>

					{contractor.bio && (
						<p className="mt-2.5 font-body text-[13px] text-dashboard-muted-dark leading-relaxed line-clamp-2 text-left">
							{contractor.bio}
						</p>
					)}
				</div>
			</div>

			{/* Stats */}
			<div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-dashboard-divider pt-4 text-xs font-mono tracking-wider text-dashboard-muted">
				<CardRating
					averageRating={contractor.averageRating}
					reviewCount={contractor.reviewCount}
				/>

				{contractor.experienceYears != null && (
					<span className="flex items-center gap-2">
						<Clock className="h-4 w-4 text-dashboard-accent-dark" />
						{contractor.experienceYears} {t("common.yrsExp").toUpperCase()}
					</span>
				)}

				{contractor.serviceAreas && contractor.serviceAreas.length > 0 && (
					<span className="flex items-center gap-2">
						<MapPin className="h-4 w-4 text-dashboard-accent-dark" />
						{contractor.serviceAreas.slice(0, 2).join(", ").toUpperCase()}
						{contractor.serviceAreas.length > 2 &&
							` +${contractor.serviceAreas.length - 2}`}
					</span>
				)}
			</div>

			{/* Trades */}
			{contractor.trades && contractor.trades.length > 0 && (
				<div className="mt-4 flex flex-wrap gap-2">
					{contractor.trades.slice(0, 3).map((trade) => (
						<span
							key={trade}
							className="inline-flex items-center gap-1 rounded-full bg-dashboard-surface border border-dashboard-border/60 px-3 py-0.5 font-body text-[11px] font-medium text-dashboard-text"
						>
							<Hammer className="h-3 w-3 text-dashboard-accent-dark" />
							{trade}
						</span>
					))}
					{contractor.trades.length > 3 && (
						<span className="rounded-full bg-dashboard-warm-100 border border-dashboard-border/40 px-2 py-0.5 font-body text-[11px] text-dashboard-muted">
							{t("common.plusMore", { count: contractor.trades.length - 3 })}
						</span>
					)}
				</div>
			)}
		</button>
	);
}

interface ContractorCardProps {
	contractor: ContractorProfile;
	/** Opens the contractor's work photo at `index` full size. */
	onOpenPhoto: (contractor: ContractorProfile, index: number) => void;
}

const PHOTO_BUTTON_CLASS =
	"cursor-zoom-in rounded-xl border-0 bg-transparent p-0";

function ContractorCard({ contractor, onOpenPhoto }: ContractorCardProps) {
	const { t } = useTranslation();
	const photos = contractor.workPhotos ?? [];

	return (
		<div className="group w-full rounded-2xl border border-dashboard-border bg-dashboard-paper p-6 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[var(--dashboard-shadow-3)]">
			<ContractorCardButton contractor={contractor} />

			{photos.length > 0 && (
				<div className="mt-4 flex gap-2">
					{photos.slice(0, 3).map((photo, index) => (
						<button
							key={photo}
							type="button"
							aria-label={t("workPhotos.open", {
								index: index + 1,
								total: photos.length,
							})}
							onClick={() => onOpenPhoto(contractor, index)}
							className={PHOTO_BUTTON_CLASS}
						>
							<img
								src={resolveMediaUrl(photo)}
								alt={`${contractor.name} work`}
								className="h-14 w-14 rounded-xl object-cover border border-dashboard-border/60 shadow-[var(--dashboard-shadow-thumbnail)]"
							/>
						</button>
					))}
					{photos.length > 3 && (
						<button
							type="button"
							aria-label={t("workPhotos.open", {
								index: 4,
								total: photos.length,
							})}
							onClick={() => onOpenPhoto(contractor, 3)}
							className={`${PHOTO_BUTTON_CLASS} flex h-14 w-14 items-center justify-center bg-dashboard-surface border border-dashboard-border/40 text-xs font-mono font-bold text-dashboard-muted shadow-[var(--dashboard-shadow-thumbnail)]`}
						>
							+{photos.length - 3}
						</button>
					)}
				</div>
			)}
		</div>
	);
}

export function BrowseContractorsPage() {
	const { t } = useTranslation();
	const CITIES = useCityFilterOptions();
	const TRADES = useTrades();
	const [contractors, setContractors] = useState<ContractorProfile[]>([]);
	const [lightbox, setLightbox] = useState<{
		contractor: ContractorProfile;
		index: number;
	} | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(false);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(0);

	// Filters
	const [searchQuery, setSearchQuery] = useState("");
	const [selectedCity, setSelectedCity] = useState("");
	const [selectedTrades, setSelectedTrades] = useState<string[]>([]);

	const toggleTrade = (trade: string) => {
		setSelectedTrades((prev) =>
			prev.includes(trade) ? prev.filter((t) => t !== trade) : [...prev, trade],
		);
	};

	const fetchContractors = useCallback(async () => {
		setIsLoading(true);
		setError(false);
		try {
			const result = await browseContractors({
				query: searchQuery || undefined,
				city: selectedCity || undefined,
				trades: selectedTrades.length > 0 ? selectedTrades : undefined,
				page,
				limit: 12,
			});
			setContractors(result.contractors);
			setTotal(result.total);
			setTotalPages(result.totalPages);
		} catch (e) {
			console.error("Failed to fetch contractors:", e);
			setError(true);
		} finally {
			setIsLoading(false);
		}
	}, [searchQuery, selectedCity, selectedTrades, page]);

	useEffect(() => {
		fetchContractors();
	}, [fetchContractors]);

	const handleSearch = (e: React.FormEvent) => {
		e.preventDefault();
		setPage(1);
		fetchContractors();
	};

	const clearFilters = () => {
		setSearchQuery("");
		setSelectedCity("");
		setSelectedTrades([]);
		setPage(1);
	};

	const hasFilters = searchQuery || selectedCity || selectedTrades.length > 0;

	return (
		<div
			className="min-h-screen pb-12"
			style={{
				background:
					"radial-gradient(ellipse at 0% 0%, color-mix(in srgb, var(--color-dashboard-accent-light) 6%, transparent) 0%, transparent 45%), radial-gradient(ellipse at 100% 100%, color-mix(in srgb, var(--color-dashboard-accent) 8%, transparent) 0%, transparent 50%), var(--color-dashboard-surface)",
			}}
		>
			{/* Header */}
			<div className="mx-auto max-w-7xl px-4 pt-10 pb-6">
				<p className="font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-dashboard-muted">
					{t("common.marketplace")}
				</p>
				<h1 className="mt-2 font-display text-[42px] font-medium leading-[1.05] tracking-[-0.02em] text-dashboard-text sm:text-[48px] lg:text-[54px]">
					{t("browse.findContractors")}
				</h1>
				<p className="mt-3 font-body text-base text-dashboard-muted max-w-2xl leading-relaxed">
					{t("browse.findContractorsSubtitle")}
				</p>
			</div>

			<div className="mx-auto max-w-7xl px-4 py-6">
				<div className="flex flex-col gap-6 lg:flex-row">
					{/* Filters Sidebar */}
					<div className="w-full lg:w-68 shrink-0">
						<div className="rounded-2xl border border-dashboard-border bg-dashboard-paper p-5 shadow-[var(--dashboard-shadow-1)]">
							<div className="flex items-center justify-between mb-5 border-b border-dashboard-divider pb-3">
								<h2 className="font-mono text-xs font-bold uppercase tracking-widest text-dashboard-text">
									{t("common.filters")}
								</h2>
								{hasFilters && (
									<button
										type="button"
										onClick={clearFilters}
										className="font-mono text-[10px] uppercase font-bold tracking-wider text-dashboard-accent-dark hover:underline"
									>
										{t("common.clearAll")}
									</button>
								)}
							</div>

							{/* Search */}
							<form onSubmit={handleSearch} className="mb-5">
								<Label className="mb-2 block font-mono text-[9px] font-bold uppercase tracking-widest text-dashboard-muted">
									{t("common.search")}
								</Label>
								<Input
									type="text"
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
									placeholder={t("browse.searchContractors")}
									className="rounded-xl border-dashboard-border bg-dashboard-bg text-dashboard-text font-body text-sm shadow-[var(--dashboard-shadow-1)] focus-visible:ring-dashboard-accent focus-visible:border-dashboard-accent"
								/>
							</form>

							{/* City */}
							<div className="mb-5">
								<Label className="mb-2 block font-mono text-[9px] font-bold uppercase tracking-widest text-dashboard-muted">
									{t("browse.city")}
								</Label>
								<Select
									value={selectedCity || "__all__"}
									onValueChange={(value) => {
										setSelectedCity(value === "__all__" ? "" : value);
										setPage(1);
									}}
								>
									<SelectTrigger className="w-full rounded-xl border-dashboard-border bg-dashboard-bg text-dashboard-text font-body text-sm shadow-[var(--dashboard-shadow-1)] focus:ring-dashboard-accent">
										<SelectValue placeholder="All Cities" />
									</SelectTrigger>
									<SelectContent className="border-dashboard-border bg-dashboard-paper">
										{CITIES.map((city) => (
											<SelectItem
												key={city.value || "__all__"}
												value={city.value || "__all__"}
												className="focus:bg-dashboard-surface focus:text-dashboard-text"
											>
												{city.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							{/* Trades */}
							<div>
								<Label className="mb-2.5 block font-mono text-[9px] font-bold uppercase tracking-widest text-dashboard-muted">
									{t("browse.trade")}
								</Label>
								<div className="flex flex-wrap gap-2">
									{TRADES.map((trade) => {
										const isSelected = selectedTrades.includes(trade.value);
										return (
											<ToggleChip
												key={trade.value}
												selected={isSelected}
												onClick={() => {
													toggleTrade(trade.value);
													setPage(1);
												}}
												className={cn(
													"px-3 py-1.5 text-xs rounded-full font-body transition-all duration-200 border",
													isSelected
														? "bg-dashboard-accent text-dashboard-on-fill border-dashboard-accent shadow-[var(--dashboard-shadow-1)]"
														: "bg-dashboard-bg border-dashboard-border text-dashboard-muted hover:bg-dashboard-surface hover:text-dashboard-text",
												)}
											>
												{trade.displayLabel}
											</ToggleChip>
										);
									})}
								</div>
							</div>
						</div>
					</div>

					{/* Results */}
					<div className="flex-1">
						{/* Results count */}
						<div className="mb-5 flex items-center justify-between">
							<p className="font-mono text-[10px] font-bold uppercase tracking-widest text-dashboard-muted">
								{isLoading
									? t("common.loading")
									: t("common.xFound", {
											count: total,
											type: t("admin.contractors").toLowerCase(),
										})}
							</p>
						</div>

						{/* Grid */}
						{isLoading ? (
							<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
								{[1, 2, 3, 4].map((i) => (
									<Skeleton
										key={i}
										className="h-52 rounded-2xl border border-dashboard-border/60 bg-dashboard-paper/40"
									/>
								))}
							</div>
						) : error ? (
							<div className="rounded-2xl border border-dashboard-border bg-dashboard-paper p-12 text-center shadow-[var(--dashboard-shadow-1)] space-y-4">
								<p className="font-body text-destructive font-semibold">
									{t("common.loadError", "Failed to load contractors")}
								</p>
								<p className="font-body text-xs text-dashboard-muted">
									There was a problem retrieving the list of contractors. Please
									check your connection and try again.
								</p>
								<button
									type="button"
									onClick={fetchContractors}
									className="mt-4 rounded-xl border border-dashboard-border bg-dashboard-paper px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-wider text-dashboard-accent-dark hover:bg-dashboard-surface shadow-[var(--dashboard-shadow-1)] transition-colors"
								>
									{t("common.retry", "Retry")}
								</button>
							</div>
						) : contractors.length === 0 ? (
							<div className="rounded-2xl border border-dashboard-border bg-dashboard-paper p-12 text-center shadow-[var(--dashboard-shadow-1)]">
								<p className="font-body text-dashboard-muted">
									{t("common.noResults")}
								</p>
								{hasFilters && (
									<button
										type="button"
										onClick={clearFilters}
										className="mt-4 font-mono text-[10px] font-bold uppercase tracking-wider text-dashboard-accent-dark hover:underline"
									>
										{t("common.clearFilters")}
									</button>
								)}
							</div>
						) : (
							<>
								<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
									{contractors.map((contractor) => (
										<ContractorCard
											key={contractor.id}
											contractor={contractor}
											onOpenPhoto={(owner, index) =>
												setLightbox({ contractor: owner, index })
											}
										/>
									))}
								</div>

								{/* Pagination */}
								{totalPages > 1 && (
									<div className="mt-8 flex items-center justify-center gap-3">
										<button
											type="button"
											onClick={() => setPage((p) => Math.max(1, p - 1))}
											disabled={page === 1}
											className="rounded-xl border border-dashboard-border bg-dashboard-paper px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-wider text-dashboard-text hover:bg-dashboard-surface disabled:opacity-50 shadow-[var(--dashboard-shadow-1)] transition-colors"
										>
											{t("common.previous")}
										</button>
										<span className="font-mono text-[11px] font-semibold text-dashboard-muted">
											{t("common.pageOf", { page, totalPages }).toUpperCase()}
										</span>
										<button
											type="button"
											onClick={() =>
												setPage((p) => Math.min(totalPages, p + 1))
											}
											disabled={page === totalPages}
											className="rounded-xl border border-dashboard-border bg-dashboard-paper px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-wider text-dashboard-text hover:bg-dashboard-surface disabled:opacity-50 shadow-[var(--dashboard-shadow-1)] transition-colors"
										>
											{t("common.next")}
										</button>
									</div>
								)}
							</>
						)}
					</div>
				</div>
			</div>
			<ImageLightbox
				images={(lightbox?.contractor.workPhotos ?? []).map((src, index) => ({
					src,
					alt: t("workPhotos.alt", {
						name: lightbox?.contractor.name ?? "",
						index: index + 1,
					}),
				}))}
				openIndex={lightbox?.index ?? null}
				onOpenIndexChange={(index) =>
					setLightbox((current) =>
						current && index !== null ? { ...current, index } : null,
					)
				}
			/>
		</div>
	);
}
